/**
 * Parcerias Router Hooks
 *
 * Custom hooks for the parcerias router (MONU-74) — typed wrappers over the
 * Amplify-IAM-signed tRPC channel. Same pattern as ./progress-hooks.ts: in
 * place because @ds/ds-api-types hasn't republished since the procedure shipped.
 *
 * Backend source:
 *   packages/serverless/modules/monumenta-trpc/handlers/parcerias/router.ts
 *
 * Endpoints:
 *   GET /monumenta-trpc/parcerias/getSummary?input=<urlEncoded(JSON)>
 *   GET /monumenta-trpc/parcerias/getDetails?input=<urlEncoded(JSON)>
 *
 * tRPC GET wire format: input MUST be a single `?input=` query param holding
 * the URL-encoded JSON-stringified input object. Flat query params (e.g.
 * ?dataInicial=20260501&...) hit the procedure with `input=undefined` and Zod
 * fails with `[{"code":"invalid_type","expected":"object","received":"undefined"}]`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { get, post } from 'aws-amplify/api'
import type {
  GetSummaryInput,
  GetSummaryOutput,
  GetDetailsInput,
  GetDetailsOutput,
  ListConfigInput,
  ListConfigOutput,
  UpsertConfigInput,
  UpsertConfigOutput,
  RemoveConfigInput,
  RemoveConfigOutput,
} from './parcerias-router.types'

// ---------------------------------------------------------------------------
// Low-level signed GET in tRPC's wire format
// ---------------------------------------------------------------------------

async function tRpcSignedGet<T>(path: string, input: unknown): Promise<T> {
  const inputJson = encodeURIComponent(JSON.stringify(input ?? {}))
  const fullPath = `${path}?input=${inputJson}`

  const apiOperation = get({
    apiName: 'monumenta-trpc',
    path: fullPath,
  })

  const response = await apiOperation.response
  const body = await response.body.text()
  const parsed = JSON.parse(body) as { result?: { data?: T }; error?: { message?: string } }

  if (parsed.error) {
    throw new Error(parsed.error.message || 'Parcerias request failed')
  }
  if (parsed.result?.data !== undefined) {
    return parsed.result.data
  }
  // Some procedures return raw — accept both shapes
  return parsed as unknown as T
}

// ---------------------------------------------------------------------------
// useParceriasSummary — landing page hero KPIs + per-axis aggregates
// ---------------------------------------------------------------------------

export function useParceriasSummary(
  input: GetSummaryInput,
  options?: { enabled?: boolean },
) {
  return useQuery<GetSummaryOutput>({
    queryKey: ['parcerias', 'getSummary', input],
    queryFn: () =>
      tRpcSignedGet<GetSummaryOutput>('/monumenta-trpc/parcerias/getSummary', input),
    enabled: options?.enabled ?? true,
    staleTime: 60_000, // 1 min — Protheus baixas don't change minute-to-minute
  })
}

// ---------------------------------------------------------------------------
// useParceriasDetails — drill-down line items for one cliente or one parceiro
// ---------------------------------------------------------------------------

export function useParceriasDetails(
  input: GetDetailsInput,
  options?: { enabled?: boolean },
) {
  return useQuery<GetDetailsOutput>({
    queryKey: ['parcerias', 'getDetails', input],
    queryFn: () =>
      tRpcSignedGet<GetDetailsOutput>('/monumenta-trpc/parcerias/getDetails', input),
    enabled: (options?.enabled ?? true) && Boolean(input.id) && Boolean(input.axis),
    staleTime: 60_000,
  })
}

// ---------------------------------------------------------------------------
// Fallback config — IAM-signed POST helper (tRPC mutations)
// ---------------------------------------------------------------------------

async function tRpcSignedPost<T>(path: string, input: unknown): Promise<T> {
  const apiOperation = post({
    apiName: 'monumenta-trpc',
    path,
    options: {
      body: (input ?? {}) as Record<string, unknown>,
    } as Parameters<typeof post>[0]['options'],
  })

  const response = await apiOperation.response
  const body = await response.body.text()
  const parsed = JSON.parse(body) as { result?: { data?: T }; error?: { message?: string } }

  if (parsed.error) {
    throw new Error(parsed.error.message || 'Parcerias mutation failed')
  }
  if (parsed.result?.data !== undefined) {
    return parsed.result.data
  }
  return parsed as unknown as T
}

// ---------------------------------------------------------------------------
// useParceriasConfig — list fallback configs (optionally filtered)
// ---------------------------------------------------------------------------

export function useParceriasConfig(
  input: ListConfigInput = {},
  options?: { enabled?: boolean },
) {
  return useQuery<ListConfigOutput>({
    queryKey: ['parcerias', 'listConfig', input],
    queryFn: () =>
      tRpcSignedGet<ListConfigOutput>('/monumenta-trpc/parcerias/listConfig', input),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  })
}

// ---------------------------------------------------------------------------
// useUpsertParceriasConfig — mark a (parceiro, cliente) tuple as fallback
// ---------------------------------------------------------------------------

export function useUpsertParceriasConfig() {
  const qc = useQueryClient()
  return useMutation<UpsertConfigOutput, Error, UpsertConfigInput>({
    mutationFn: (input) =>
      tRpcSignedPost<UpsertConfigOutput>('/monumenta-trpc/parcerias/upsertConfig', input),
    onSuccess: () => {
      // Invalidate everything parcerias — config changes affect summary/details
      // because zero-zero rows now get classified as fallback.
      qc.invalidateQueries({ queryKey: ['parcerias'] })
    },
  })
}

// ---------------------------------------------------------------------------
// useRemoveParceriasConfig — drop a config entry
// ---------------------------------------------------------------------------

export function useRemoveParceriasConfig() {
  const qc = useQueryClient()
  return useMutation<RemoveConfigOutput, Error, RemoveConfigInput>({
    mutationFn: (input) =>
      tRpcSignedPost<RemoveConfigOutput>('/monumenta-trpc/parcerias/removeConfig', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parcerias'] })
    },
  })
}
